// Recent and Favorite operations — per browser (= the admin using it), in localStorage.
// Every read/write is guarded: without storage (private mode, blocked site data) the
// sections are simply empty and nothing throws.

import { useEffect, useState } from 'react'

const RECENT_KEY = 'vulpio.ops.recent.v1'
const FAV_KEY = 'vulpio.ops.favorites.v1'
const RECENT_MAX = 8
const EVENT = 'vulpio:op-prefs'

function read(key: string): string[] {
  try {
    const raw = localStorage.getItem(key)
    const v: unknown = raw ? JSON.parse(raw) : []
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []
  } catch {
    return []
  }
}

function write(key: string, ids: string[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(ids))
  } catch {
    // storage unavailable — the list just isn't remembered
  }
  window.dispatchEvent(new Event(EVENT))
}

export const getRecent = (): string[] => read(RECENT_KEY)
export const getFavorites = (): string[] => read(FAV_KEY)

/** Most recent first, deduplicated, capped. Called when an operation is run/previewed. */
export function pushRecent(operationId: string): void {
  write(RECENT_KEY, [operationId, ...getRecent().filter((x) => x !== operationId)].slice(0, RECENT_MAX))
}

export function toggleFavorite(operationId: string): void {
  const favs = getFavorites()
  write(FAV_KEY, favs.includes(operationId) ? favs.filter((x) => x !== operationId) : [...favs, operationId])
}

/** Live { recent, favorites } — re-reads on our own writes and on other tabs' changes. */
export function useOpPrefs(): { recent: string[]; favorites: string[] } {
  const [state, setState] = useState(() => ({ recent: getRecent(), favorites: getFavorites() }))
  useEffect(() => {
    const sync = () => setState({ recent: getRecent(), favorites: getFavorites() })
    window.addEventListener(EVENT, sync)
    window.addEventListener('storage', sync)
    return () => {
      window.removeEventListener(EVENT, sync)
      window.removeEventListener('storage', sync)
    }
  }, [])
  return state
}
